import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';
import { Section965Service } from '../../section965.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';

interface Section965TaxRow {
  ROW_ORDER: number;
  ROW_NAME: string;
  TOTAL_AMOUNT: number;
  GE_TOTAL_AMOUNT: number;
  GECS_TOTAL_AMOUNT: number;
  GHOST_SCENARIO: number;
  ELECTION_TYPE_CNT: number;
  ELECTION_TYPE_NUMBER: number;
  ELECTION_TYPE: string;
}

@Component({
    selector: 'gtw-section-965-tax-db',
    templateUrl: './section-965-tax-db.component.html',
    styleUrls: ['./section-965-tax-db.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Section965TaxDbComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  private isInitialized = false;
  private isLoading = false;

  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};

  showFormLoader = true;
  show965Table = false;
  crudLoading = false;

  schdData: Section965TaxRow[] = [];
  electionLines: Array<{ ELECTIONNO: number; ROW_ORDER: number; VALUE: string; GHOST_SCENARIO: number }> = [];
  election: Record<number, string> = {};
  invalidTotalByRow: Record<number, boolean> = {};

  TOTAL_AGG_FOREIGN_CASH = 0;
  TOTAL_NET_SECTION965H = 0;
  TOTAL_NET_SECTION965I = 0;
  GE_AGG_FOREIGN_CASH = 0;
  GE_TOTAL_NET_SECTION965H = 0;
  GE_TOTAL_NET_SECTION965I = 0;
  GECS_AGG_FOREIGN_CASH = 0;
  GECS_TOTAL_NET_SECTION965H = 0;
  GECS_TOTAL_NET_SECTION965I = 0;
  GHOST_SCENARIO = 0;

  constructor(
    private cdr: ChangeDetectorRef,
    private section965Service: Section965Service,
    private gtwSnackbar: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    this.isLoading = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    if (!this.GENERAL_CONFIG?.base_url) {
      return;
    }
    this.isInitialized = true;
    this.loadFormData();
  }

  private loadFormData(): void {
    this.isLoading = true;
    this.showFormLoader = true;
    this.show965Table = false;
    this.section965Service.getTaxStatementData(
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (rows: Section965TaxRow[]) => {
        this.processRows(rows || []);
        this.showFormLoader = false;
        this.show965Table = true;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.showFormLoader = false;
        this.show965Table = false;
        this.isLoading = false;
        this.gtwSnackbar.openSnackBar('Error loading Section 965 data', 'gtw-snackbar--red', true, 4);
        this.cdr.detectChanges();
      }
    });
  }

  private processRows(rows: Section965TaxRow[]): void {
    this.schdData = rows;
    this.electionLines = [];
    this.election = {};
    this.invalidTotalByRow = {};
    let startLoop = 1;
    const endLoop = 5;

    for (let i = 0; i < this.schdData.length; i++) {
      const value = this.schdData[i];
      if (value.ROW_ORDER === 2) {
        this.TOTAL_AGG_FOREIGN_CASH = Number(value.TOTAL_AMOUNT || 0);
        this.GE_AGG_FOREIGN_CASH = Number(value.GE_TOTAL_AMOUNT || 0);
        this.GECS_AGG_FOREIGN_CASH = Number(value.GECS_TOTAL_AMOUNT || 0);
      } else if (value.ROW_ORDER === 7) {
        this.TOTAL_NET_SECTION965H = Number(value.TOTAL_AMOUNT || 0);
        this.GE_TOTAL_NET_SECTION965H = Number(value.GE_TOTAL_AMOUNT || 0);
        this.GECS_TOTAL_NET_SECTION965H = Number(value.GECS_TOTAL_AMOUNT || 0);
      } else if (value.ROW_ORDER === 8) {
        this.TOTAL_NET_SECTION965I = Number(value.TOTAL_AMOUNT || 0);
        this.GE_TOTAL_NET_SECTION965I = Number(value.GE_TOTAL_AMOUNT || 0);
        this.GECS_TOTAL_NET_SECTION965I = Number(value.GECS_TOTAL_AMOUNT || 0);
      } else if (value.ROW_ORDER === 9 && value.ELECTION_TYPE_CNT > 0) {
        this.electionLines.push({
          ELECTIONNO: value.ELECTION_TYPE_NUMBER,
          ROW_ORDER: 9,
          VALUE: value.ELECTION_TYPE,
          GHOST_SCENARIO: value.GHOST_SCENARIO
        });
        this.election[value.ELECTION_TYPE_NUMBER] = value.ELECTION_TYPE;
        startLoop = value.ELECTION_TYPE_NUMBER + 1;
      }
      this.GHOST_SCENARIO = value.GHOST_SCENARIO;
    }

    if (startLoop <= endLoop) {
      for (let i = startLoop; i <= endLoop; i++) {
        this.electionLines.push({
          ELECTIONNO: i,
          ROW_ORDER: 9,
          VALUE: '',
          GHOST_SCENARIO: this.GHOST_SCENARIO
        });
        this.election[i] = '';
      }
    }
  }

  private rowOrderValue(rowOrder: number): { total: number; ge: number; gecs: number; electionType: string } {
    if (rowOrder === 2) {
      return {
        total: Number(this.TOTAL_AGG_FOREIGN_CASH || 0),
        ge: Number(this.GE_AGG_FOREIGN_CASH || 0),
        gecs: Number(this.GECS_AGG_FOREIGN_CASH || 0),
        electionType: 'NA'
      };
    }
    if (rowOrder === 7) {
      return {
        total: Number(this.TOTAL_NET_SECTION965H || 0),
        ge: Number(this.GE_TOTAL_NET_SECTION965H || 0),
        gecs: Number(this.GECS_TOTAL_NET_SECTION965H || 0),
        electionType: 'NA'
      };
    }
    if (rowOrder === 9) {
      const electionValues: string[] = [];
      for (let i = 0; i < this.electionLines.length; i++) {
        const e = this.election[i + 1];
        if (e !== undefined && e !== null && String(e).trim() !== '') {
          electionValues.push(String(e).trim());
        }
      }
      return {
        total: 0,
        ge: 0,
        gecs: 0,
        electionType: electionValues.length > 0 ? electionValues.join(', ') : ''
      };
    }
    return {
      total: Number(this.TOTAL_NET_SECTION965I || 0),
      ge: Number(this.GE_TOTAL_NET_SECTION965I || 0),
      gecs: Number(this.GECS_TOTAL_NET_SECTION965I || 0),
      electionType: 'NA'
    };
  }

  private validateTotalAmount(rowOrder: number, total: number, ge: number, gecs: number): boolean {
    const ok = total === ge + gecs;
    this.invalidTotalByRow[rowOrder] = !ok;
    return ok;
  }

  save(row: any): void {
    const rowOrder = Number(row?.ROW_ORDER || 0);
    const ghostScenario = Number(row?.GHOST_SCENARIO || this.GHOST_SCENARIO || 0);
    const values = this.rowOrderValue(rowOrder);

    if (this.crudLoading) {
      this.gtwSnackbar.openSnackBar('Please wait while we save this request', 'gtw-snackbar--orange', true, 4);
      return;
    }

    if (rowOrder !== 9 && !this.validateTotalAmount(rowOrder, values.total, values.ge, values.gecs)) {
      this.cdr.detectChanges();
      return;
    }

    this.crudLoading = true;
    this.cdr.detectChanges();

    this.section965Service.saveTaxStatementData(
      this.GENERAL_CONFIG.base_url,
      {
        ghost_scenario: ghostScenario,
        row_order: rowOrder,
        total_amt: values.total,
        ge_total_amt: values.ge,
        gecs_total_amt: values.gecs,
        election_type: values.electionType
      },
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result: any) => {
        this.crudLoading = false;
        if (result?.callSuccess !== '1') {
          this.gtwSnackbar.openSnackBar(result?.errorMessage || 'Save failed', 'gtw-snackbar--red', true, 4);
        } else if (rowOrder === 9) {
          this.gtwSnackbar.openSnackBar('Elections have been saved successfully', 'gtw-snackbar--green', true, 4);
          this.loadFormData();
        } else {
          this.gtwSnackbar.openSnackBar('Amounts have been saved successfully', 'gtw-snackbar--green', true, 4);
          this.loadFormData();
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.crudLoading = false;
        this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
        this.cdr.detectChanges();
      }
    });
  }
}
